import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { computed } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import {
  EnumAssetAdvancedProcessing,
  EnumAssetVectorEmbeddings,
} from "../../__generated__/graphql";
import { useModalContext } from "../../contexts/modals";
import useStores from "../../hooks/useStores";
import { AttachmentIcon } from "../../icon-imports";
import Asset from "../../models/Asset";
import { PricingPlanTier } from "../../models/User";
import { retryFileAttachmentProcessing } from "../../utils/ProcessAttachments";
import AudioInput from "../Input/AudioInput";
import Tooltip from "../Tooltip";
import AttachmentProgress from "./AttachmentProgress";
import ChatAttachmentInput from "./ChatAttachmentInput";
import CompactAttachmentBar from "./CompactAttachmentBar";
import UploadProgress from "./UploadProgress";

// Custom Home Chat Input Component - Redesigned
const HomeChatInput = ({
  text,
  onTextChange,
  onSend,
  assetIds,
  setAssetIds,
  chatFileUploadUsesLeft: _chatFileUploadUsesLeft, // PRO plan gating temporarily disabled
  currentPlan: _currentPlan, // PRO plan gating temporarily disabled
  disabled,
}: {
  text: string;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: (args?: { query?: { prompt?: string } }) => void;
  assetIds: string[];
  setAssetIds: (ids: string[] | ((prev: string[]) => string[])) => void;
  chatFileUploadUsesLeft: number;
  currentPlan: PricingPlanTier;
  disabled?: boolean;
}) => {
  const { assets } = useStores();
  const { numOpenModals } = useModalContext();
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [uploadingAssets, setUploadingAssets] = React.useState<
    { id: string; name: string; progress: number }[]
  >([]);

  // Get assets that are currently being processed (show in dialog)
  const processingAssets = computed(() => {
    if (!assets) return [];
    return assetIds
      .map((id) => assets.get(id))
      .filter(
        (asset): asset is Asset =>
          asset !== undefined &&
          (asset.vectorEmbeddings === EnumAssetVectorEmbeddings.InProgress ||
            asset.vectorEmbeddings === EnumAssetVectorEmbeddings.Error ||
            asset.advancedProcessing ===
              EnumAssetAdvancedProcessing.InProgress ||
            asset.advancedProcessing === EnumAssetAdvancedProcessing.Error)
      );
  });

  // Get all attached assets (show inline as previews)
  // Show all assets regardless of processing status - pills should always be visible
  const attachedAssets = computed(() => {
    if (!assets) return [];
    return assetIds
      .map((id) => assets.get(id))
      .filter((asset): asset is Asset => asset !== undefined);
  });

  React.useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [text]);

  // Maintain focus after sending message
  React.useEffect(() => {
    if (textareaRef.current && text === "") {
      // Focus the textarea after text is cleared (message sent)
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  }, [text]);

  const handleSend = () => {
    if (!text || text.trim() === "" || disabled) return;
    // Don't pass prompt - let parent read from text state (like old ChatMessageInput)
    onSend();
  };

  const isSubmitDisabled = !text.trim() || disabled;

  return (
    <div className="relative">
      {/* Show upload progress modal */}
      {numOpenModals === 0 && uploadingAssets.length > 0 && (
        <Dialog.Root open={true}>
          <Dialog.Portal>
            <Dialog.Overlay className="z-[1003] fixed inset-0 bg-blackA6 data-[state=open]:animate-overlayShow" />
            <Dialog.Content className="z-[1004] fixed left-1/2 top-1/2 max-h-[85vh] max-w-[90vw] min-w-[750px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
              <UploadProgress assets={uploadingAssets} />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Show processing progress modal */}
      {numOpenModals === 0 && processingAssets.get().length > 0 && (
        <Dialog.Root open={true}>
          <Dialog.Portal>
            <Dialog.Overlay className="z-[1001] fixed inset-0 bg-blackA6 data-[state=open]:animate-overlayShow" />
            <Dialog.Content className="z-[1002] fixed left-1/2 top-1/2 max-h-[85vh] max-w-[90vw] min-w-[750px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
              <AttachmentProgress
                assets={processingAssets.get()}
                onRetry={async (assetId) => {
                  assets.save({
                    id: assetId,
                    vectorEmbeddings: EnumAssetVectorEmbeddings.InProgress,
                    advancedProcessing: EnumAssetAdvancedProcessing.InProgress,
                  });
                  await retryFileAttachmentProcessing(assetId);
                }}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Container with gradient blur effect */}
      <div className="relative group">
        {/* Gradient blur background */}
        <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity" />

        <div
          className="relative flex flex-col bg-white rounded-2xl border border-gray-200 shadow-lg shadow-gray-200/50 overflow-hidden cursor-text"
          onClick={() => {
            if (textareaRef.current) {
              textareaRef.current.focus();
            }
          }}
        >
          {/* Main input area */}
          <div className="flex-1 flex items-start px-6 py-5">
            {/* Search icon */}
            <svg
              className="w-6 h-6 text-gray-400 mr-4 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={onTextChange}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Create a lesson plan for photosynthesis for grade 7..."
              className="w-full bg-transparent border-none outline-none resize-none text-lg text-gray-700 placeholder-gray-400 py-0"
              rows={1}
              style={{ maxHeight: "120px" }}
            />
          </div>

          {/* Attached files - inside input */}
          {attachedAssets.get().length > 0 && (
            <div className="px-6 pb-3">
              <CompactAttachmentBar
                assets={attachedAssets.get()}
                onRemove={(id) => {
                  setAssetIds((prev) => prev.filter((aid) => aid !== id));
                }}
              />
            </div>
          )}

          {/* Bottom bar with icons and send button */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            {/* Left side icons - Attachment and Audio */}
            <div className="flex items-center gap-1">
              {/* Attachment Icon */}
              {/* PRO plan gating temporarily disabled */}
              {assetIds.length >= 10 ? (
                <Tooltip
                  content="Maximum 10 attachments allowed"
                  sideOffset={5}
                >
                  <button
                    type="button"
                    className="p-2 text-gray-400 cursor-not-allowed rounded-lg"
                    disabled
                    onClick={(e) => e.stopPropagation()}
                  >
                    <AttachmentIcon className="h-6 w-6" />
                  </button>
                </Tooltip>
              ) : (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center"
                >
                  <ChatAttachmentInput
                    assetIds={assetIds}
                    setAssetIds={setAssetIds}
                    setUploadingAssets={setUploadingAssets}
                    disabled={disabled}
                    buttonClassName="p-2 border-0 rounded-lg hover:bg-gray-100"
                  />
                </div>
              )}

              {/* Audio Input Icon */}
              <Tooltip content="Speech to Text" sideOffset={5}>
                <div className="flex" onClick={(e) => e.stopPropagation()}>
                  <AudioInput
                    disabled={disabled}
                    setTranscript={(transcript: string) => {
                      if (disabled) return;
                      onTextChange({
                        target: { value: transcript },
                      } as React.ChangeEvent<HTMLTextAreaElement>);
                    }}
                  />
                </div>
              </Tooltip>
            </div>

            {/* Send Button - styled like landing page */}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSend();
              }}
              disabled={isSubmitDisabled}
              className={clsx(
                "px-5 py-2.5 rounded-full font-semibold flex items-center gap-2 transition-all flex-shrink-0",
                isSubmitDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-[#3F8EF7] text-white hover:bg-gray-800 hover:scale-105 active:scale-95"
              )}
              title="Send message (Enter)"
            >
              {/* Sparkles icon */}
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
                <path d="M5 3v4" />
                <path d="M19 17v4" />
                <path d="M3 5h4" />
                <path d="M17 19h4" />
              </svg>
              Ask Alayna
            </button>
          </div>
        </div>
      </div>

      {/* Powered by text */}
      <p className="text-center text-sm text-gray-500 mt-4">
        Powered by the world's most capable AI models, fine-tuned for education
      </p>
    </div>
  );
};

export default observer(HomeChatInput);
